import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface TailoringDebugData {
  timestamp: string;
  resumeText: string;
  jobDescription: string;
  requestBody: any;
  response?: any;
  error?: string;
}

interface TailoringDebugViewerProps {
  debugData: TailoringDebugData[];
}

export const TailoringDebugViewer: React.FC<TailoringDebugViewerProps> = ({ debugData }) => {
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const { toast } = useToast();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: `${label} copied successfully`,
    });
  };

  const formatJson = (obj: any) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Tailoring Request Debug Log</h3>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{debugData.length} requests logged</Badge>
          {debugData.length > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                localStorage.removeItem('tailoring-debug-data');
                window.location.reload();
              }}
            >
              Clear Debug Data
            </Button>
          )}
        </div>
      </div>

      {debugData.map((data, index) => (
        <Card key={index} className="w-full">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">
                Request #{index + 1} - {data.timestamp}
              </CardTitle>
              <div className="flex items-center gap-2">
                {data.error && (
                  <Badge variant="destructive">Error</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedItem(expandedItem === index ? null : index)}
                >
                  {expandedItem === index ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>

          {expandedItem === index && (
            <CardContent>
              <Tabs defaultValue="summary" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="resume">Resume Text</TabsTrigger>
                  <TabsTrigger value="job">Job Description</TabsTrigger>
                  <TabsTrigger value="request">Request Body</TabsTrigger>
                  <TabsTrigger value="response">Response</TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>Resume Length:</strong> {data.resumeText.length.toLocaleString()} characters
                    </div>
                    <div>
                      <strong>Job Description Length:</strong> {data.jobDescription.length.toLocaleString()} characters
                    </div>
                    <div>
                      <strong>Pipeline ID:</strong> {data.requestBody?.pipeline_id || 'Unknown'}
                    </div>
                    <div>
                      <strong>Status:</strong> {data.error ? 'Failed' : 'Success'}
                    </div>
                  </div>
                  {data.error && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded">
                      <strong className="text-destructive">Error:</strong> {data.error}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="resume" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Resume Text ({data.resumeText.length} chars)</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(data.resumeText, 'Resume text')}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <Textarea
                    value={data.resumeText}
                    readOnly
                    className="min-h-[300px] font-mono text-sm"
                  />
                </TabsContent>

                <TabsContent value="job" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Job Description ({data.jobDescription.length} chars)</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(data.jobDescription, 'Job description')}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <Textarea
                    value={data.jobDescription}
                    readOnly
                    className="min-h-[300px] font-mono text-sm"
                  />
                </TabsContent>

                <TabsContent value="request" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">VectorShift Request Body</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(formatJson(data.requestBody), 'Request body')}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy JSON
                    </Button>
                  </div>
                  <Textarea
                    value={formatJson(data.requestBody)}
                    readOnly
                    className="min-h-[300px] font-mono text-sm"
                  />
                </TabsContent>

                <TabsContent value="response" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">VectorShift Response</h4>
                    {data.response && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(formatJson(data.response), 'Response')}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy JSON
                      </Button>
                    )}
                  </div>
                  <Textarea
                    value={data.response ? formatJson(data.response) : 'No response data available'}
                    readOnly
                    className="min-h-[300px] font-mono text-sm"
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          )}
        </Card>
      ))}

      {debugData.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">No debug data available. Submit a tailoring request to see debug information.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};